/**
 * Plan acknowledgments (web): API paths, labels and formatting shared by the
 * staff "Plans to read" list, the plan reader, the documenting warning and the
 * coordinators' coverage view. The server decides who must read what
 * (services/program245dPlanAcknowledgments); nothing here blocks documenting.
 */

export const PLAN_ACK_ROOT = '/245d-operations/plan-acknowledgments';
export const MY_PLANS_PATH = `${PLAN_ACK_ROOT}/mine`;
export const planPath = (documentId) => `${PLAN_ACK_ROOT}/documents/${encodeURIComponent(documentId)}`;
export const acknowledgePath = (documentId) => `${planPath(documentId)}/acknowledge`;
export const coveragePath = (programId) => `/245d-operations/${encodeURIComponent(programId)}/plan-acknowledgments`;

// Approved single-color badges (CLAUDE.md): readable in light and dark mode.
export const STATE_BADGE = Object.freeze({
  overdue: 'bg-red-700 text-red-100',
  pending: 'bg-amber-600 text-amber-100',
  acknowledged: 'bg-emerald-700 text-emerald-100',
  no_staff: 'bg-slate-700 text-slate-100',
});
export const STATE_LABEL = Object.freeze({
  overdue: 'Overdue',
  pending: 'Pending',
  acknowledged: 'Acknowledged',
  no_staff: 'No staff assigned',
});
export const STATE_FILTERS = Object.freeze([
  { value: 'outstanding', label: 'Pending or overdue' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'pending', label: 'Pending' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'all', label: 'All' },
]);

export const personName = (person) => `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

/** A calendar day (YYYY-MM-DD) as M/D/YYYY, without any time zone shift. */
export function formatDay(day) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  return match ? `${Number(match[2])}/${Number(match[3])}/${match[1]}` : '';
}

/** A timestamp in the viewer's own time: M/D/YYYY h:mm AM. */
export function formatTimestamp(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const hours = date.getHours();
  const period = hours >= 12 ? 'PM' : 'AM';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${hours % 12 || 12}:${String(date.getMinutes()).padStart(2, '0')} ${period}`;
}

/** "Due 10/27/2026" or "Was due 10/20/2026" for an outstanding plan. */
export function dueText(row) {
  if (!row?.dueDate) return '';
  return row.state === 'overdue' ? `Was due ${formatDay(row.dueDate)}` : `Due ${formatDay(row.dueDate)}`;
}

/** Refused, not offered or not available yet: show nothing rather than an error. */
export const isNotOffered = (error) => [403, 404, 503].includes(error?.response?.status);

export const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;
